<template>
  <aside
    class="popup-form updates-center-popup"
    :class="{'updates-center-popup--capture-hidden': captureHidden}"
  >
    <div class="updates-center-content">
      <header class="updates-center-header">
        <button class="updates-close" type="button" aria-label="Zavrieť" @click="closePopup">
          <app-icon name="close" :size="24"/>
        </button>
        <span class="updates-kicker">Čo je nové v Bankároch</span>
        <h2>Novinky a podpora</h2>
        <p>Prehľad zmien v aplikácii, metodike bánk a nové odborné články.</p>
      </header>

      <nav class="updates-tabs" aria-label="Novinky a spätná väzba">
        <button
          type="button"
          :class="{active: activeTab === 'updates'}"
          :aria-selected="activeTab === 'updates'"
          @click="activeTab = 'updates'"
        >
          <app-icon name="sparkles" :size="19"/>
          Novinky
        </button>
        <button
          type="button"
          :class="{active: activeTab === 'feedback'}"
          :aria-selected="activeTab === 'feedback'"
          @click="activeTab = 'feedback'"
        >
          <app-icon name="feedback" :size="19"/>
          Nahlásiť problém
        </button>
      </nav>

      <section v-if="activeTab === 'updates'" class="updates-feed" aria-label="Zmeny v aplikácii">
        <article
          v-for="item in updates"
          :key="item.id"
          class="update-card"
          :class="`update-card--${item.type}`"
        >
          <div class="update-card-meta">
            <span class="update-card-label">{{ item.label }}</span>
            <span v-if="item.bank" class="update-card-bank">{{ item.bank }}</span>
            <time>{{ item.date }}</time>
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.summary }}</p>
          <a
            v-if="item.link"
            class="update-card-link"
            :href="item.link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ item.linkLabel || "Zistiť viac" }}
            <app-icon name="arrow-right" :size="17"/>
          </a>
        </article>
      </section>

      <user-feedback-pop-up
        v-else
        embedded
        @capture-state="captureHidden = $event"
      />
    </div>
  </aside>
</template>

<script>
import AppIcon from "@/components/shared/AppIcon.vue";
import UserFeedbackPopUp from "@/components/shared/PopupHandler/Popups/UserFeedback/UserFeedbackPopUp.vue";
import {latestProductUpdateId, productUpdates} from "@/data/productUpdates";

export default {
  name: "ProductUpdatesCenterPopUp",
  components: {AppIcon, UserFeedbackPopUp},
  data() {
    return {
      activeTab: "updates",
      captureHidden: false,
      updates: productUpdates,
    };
  },
  mounted() {
    window.localStorage.setItem("bankari-latest-product-update", latestProductUpdateId);
    window.dispatchEvent(new CustomEvent("bankari-product-updates-seen"));
  },
  methods: {
    closePopup() {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
  },
};
</script>

<style scoped lang="scss">
.updates-center-popup {
  width: min(580px, 100vw);
  background: var(--app-background);
  color: var(--app-text);
  transition: none;
}

.updates-center-popup--capture-hidden {
  opacity: 0;
  pointer-events: none;
}

.updates-center-content {
  min-height: 100%;
  padding: 30px;
}

.updates-center-header {
  position: relative;
  padding-right: 44px;
}

.updates-center-header h2 {
  margin: 5px 0 8px;
  font-size: 29px;
  line-height: 1.2;
}

.updates-center-header p {
  margin: 0;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 1.55;
}

.updates-kicker {
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.updates-close {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  cursor: pointer;
}

.updates-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin: 24px 0 20px;
  padding: 5px;
  border: 1px solid var(--app-border);
  border-radius: 16px;
  background: var(--app-surface-muted);
}

.updates-tabs button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: var(--app-text-muted);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.updates-tabs button.active {
  border-color: var(--app-border);
  background: var(--app-surface);
  color: var(--app-primary);
  box-shadow: 0 8px 20px rgba(4, 17, 36, .16);
}

.updates-feed {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 34px;
}

.update-card {
  padding: 17px 18px;
  border: 1px solid var(--app-border);
  border-left: 4px solid var(--app-primary);
  border-radius: 15px;
  background: var(--app-surface-muted);
}

.update-card--methodology {
  border-left-color: #e4a546;
}

.update-card--article {
  position: relative;
  overflow: hidden;
  border-color: color-mix(in srgb, var(--app-primary) 46%, var(--app-border));
  border-left-color: #54c997;
  background: linear-gradient(135deg, color-mix(in srgb, var(--app-primary) 14%, var(--app-surface)) 0%, var(--app-surface-muted) 72%);
}

.update-card--article::after {
  position: absolute;
  top: -34px;
  right: -28px;
  width: 110px;
  height: 110px;
  border: 18px solid color-mix(in srgb, var(--app-primary) 10%, transparent);
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.update-card-meta {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}

.update-card-label,
.update-card-bank {
  padding: 4px 7px;
  border-radius: 7px;
  background: color-mix(in srgb, var(--app-primary) 15%, var(--app-surface));
  color: var(--app-primary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .03em;
  text-transform: uppercase;
}

.update-card--methodology .update-card-label {
  background: rgba(228, 165, 70, .14);
  color: #e4a546;
}

.update-card--article .update-card-label {
  background: rgba(84, 201, 151, .14);
  color: #54c997;
}

.update-card-bank {
  background: var(--app-surface);
  color: var(--app-text);
}

.update-card time {
  margin-left: auto;
  color: var(--app-text-muted);
  font-size: 11px;
}

.update-card h3 {
  position: relative;
  z-index: 1;
  margin: 10px 0 6px;
  font-size: 16px;
  line-height: 1.35;
}

.update-card p {
  position: relative;
  z-index: 1;
  margin: 0;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.55;
}

.update-card-link {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
}

.update-card-link:hover {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .updates-center-content { padding: 22px 18px 90px; }
  .updates-tabs { grid-template-columns: 1fr; }
  .update-card time { width: 100%; margin-left: 0; }
}
</style>
